var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { Box, Button, ButtonGroup, Fab, ListItemIcon, ListItemText, Menu, MenuItem, Zoom } from '@material-ui/core';
import { Add, CloudUpload, CloudUploadOutlined, Folder, Forward, MoreHoriz, ViewColumn, ViewList } from '@material-ui/icons';
import { AppSnackbar, PageTemplate, PageTemplateBodyCard, PageTemplateDashboard, PageTemplateHeader, SearchBox } from '@sierralabs/core-ui';
import { TaskBoard, useFindTaskTypes, useWorkflowContext } from '@sierralabs/workflow-ui';
import PageTemplateAlert from '../common/page-template/PageTemplateAlert';
import DocumentBreadcrumbs from './components/DocumentBreadcrumbs';
import DocumentFilter from './components/DocumentFilter';
import DocumentInfoDrawer from './components/DocumentInfoDrawer';
import DocumentList from './components/DocumentList';
import CreateFolderDialog from './dialogs/CreateFolderDialog';
import DocumentMoveDialog from './dialogs/DocumentMoveDialog';
import FileUploadDialog from './dialogs/FileUploadDialog';
import FolderUploadDialog from './dialogs/FolderUploadDialog';
import { useFindFiles } from './hooks/useFindFiles';
import { useFolderId } from './hooks/useFolderId';
import { useDocumentListPageContext } from './DocumentListPageContext';
var ViewEnum;
(function (ViewEnum) {
    ViewEnum["List"] = "list";
    ViewEnum["Board"] = "board";
})(ViewEnum || (ViewEnum = {}));
const StyledContainer = styled.div `
  flex: 1;
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
  .page-template-body {
    ${props => props.theme.breakpoints.up('md')} {
      margin: 0 -30px;
    }
  }
`;
const StyledFab = styled(Fab) `
  position: absolute;
  top: 82px;
  left: 26px;
  z-index: 3;
`;
const StyledTitle = styled.div `
  display: flex;
  flex-direction: column;
`;
const zoomProps = { in: true, timeout: 300 };
const DocumentListPage = props => {
    const { className, containerProps } = props;
    const history = useHistory();
    const { basePath, toFilePath } = useDocumentListPageContext();
    const { setError: setWorkflowError } = useWorkflowContext();
    const { folderId: paramFolderId } = useParams();
    const folderId = useFolderId(paramFolderId);
    // query params
    const [view, setView] = useQueryParam('view', withDefault(StringParam, ViewEnum.List));
    const [search, setSearch] = useQueryParam('search', withDefault(StringParam, ''));
    const [orderParam, setOrder] = useQueryParam('order', withDefault(StringParam, 'ASC'));
    const [orderBy, setOrderBy] = useQueryParam('orderBy', withDefault(StringParam, 'file.name'));
    const order = ['ASC', 'DESC'].includes(orderParam)
        ? orderParam
        : 'ASC';
    // dialogs
    const [isOpenDrawer, setIsOpenDrawer] = useState(false);
    const [isOpenMoveDialog, setIsOpenMoveDialog] = useState(false);
    const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
    const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
    const folderInputRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selected, setSelected] = useState();
    const [taskTypeId, setTaskTypeId] = useState();
    const [selectedIds, setSelectedIds] = useState([]);
    const [error, setError] = useState();
    // params used for board view
    const [apiParams, setApiParams] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const { files, folder, isLoading, error: findError, findFiles } = useFindFiles(folderId);
    const { data: taskTypes, findTaskTypes } = useFindTaskTypes();
    const hasSelected = selectedIds.length > 0;
    const menuItems = useMemo(() => {
        const menuItems = [
            {
                title: selectedIds.length === 1 ? 'Move File' : 'Move Files',
                hidden: !hasSelected,
                icon: Forward,
                onClick: () => setIsOpenMoveDialog(true)
            },
            {
                title: 'New Folder',
                icon: Folder,
                onClick: () => setIsOpenCreateDialog(true)
            },
            {
                title: 'Upload Files',
                disabled: !folder,
                icon: CloudUpload,
                onClick: () => setIsOpenUploadDialog(true)
            },
            {
                title: 'Upload Folder',
                disabled: !folder,
                icon: CloudUploadOutlined,
                onClick: () => { var _a; return (_a = folderInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }
            }
        ];
        return menuItems.filter(item => !item.hidden);
    }, [folder, hasSelected, selectedIds.length]);
    const findFilesWithParams = useCallback((params) => {
        findFiles(Object.assign({ withChildren: true, search }, params));
    }, [search, findFiles]);
    const closeDrawer = () => {
        setSelected(undefined);
        setIsOpenDrawer(false);
    };
    const handleProgressCount = (progress, shouldFetch = true) => {
        const { success } = progress;
        if (success > 0) {
            setSuccessMessage(`Number of files uploaded: ${success}`);
            shouldFetch && findFilesWithParams();
        }
    };
    const refreshList = useCallback(() => {
        // reset selected files and refetch
        setSelectedIds([]);
        setSelected(undefined);
        findFilesWithParams();
    }, [findFilesWithParams]);
    useEffect(() => {
        findTaskTypes({ entityTable: 'file' });
    }, [findTaskTypes]);
    useEffect(() => {
        var _a;
        if (taskTypes) {
            setTaskTypeId((_a = taskTypes === null || taskTypes === void 0 ? void 0 : taskTypes[0]) === null || _a === void 0 ? void 0 : _a.id);
        }
    }, [taskTypes]);
    useEffect(() => {
        setSelected(undefined);
    }, [view]);
    // refresh list on page change
    useEffect(() => {
        refreshList();
    }, [search, folderId, refreshList]);
    useEffect(() => {
        setError(findError);
    }, [findError]);
    useEffect(() => {
        // hide workflow warning on close drawer
        if (!isOpenDrawer || (selected === null || selected === void 0 ? void 0 : selected.isFolder)) {
            setWorkflowError(undefined);
        }
    }, [isOpenDrawer, selected, setWorkflowError]);
    useEffect(() => {
        setApiParams(params => {
            if (!search) {
                if (params.search) {
                    params = Object.assign({}, params);
                    delete params.search;
                }
                return params;
            }
            return Object.assign(Object.assign({}, params), { search });
        });
    }, [search]);
    return (React.createElement(StyledContainer, Object.assign({ className: className }, containerProps),
        view !== ViewEnum.Board && (React.createElement(StyledFab, { color: 'primary', onClick: e => setAnchorEl(e.currentTarget) },
            hasSelected && (React.createElement(Zoom, Object.assign({}, zoomProps),
                React.createElement(MoreHoriz, null))),
            !hasSelected && (React.createElement(Zoom, Object.assign({}, zoomProps),
                React.createElement(Add, null))))),
        React.createElement(Menu, { id: 'document-menu', keepMounted: true, anchorEl: anchorEl, open: !!anchorEl, onClose: () => setAnchorEl(null) }, menuItems.map((item, index) => (React.createElement(MenuItem, { key: index, disabled: item.disabled, onClick: () => {
                item.onClick();
                setAnchorEl(null);
            } },
            React.createElement(ListItemIcon, null,
                React.createElement(item.icon, { fontSize: 'small' })),
            React.createElement(ListItemText, null, item.title))))),
        folder && (React.createElement(FileUploadDialog, { folder: folder, open: isOpenUploadDialog, onDismiss: handleProgressCount, setOpen: setIsOpenUploadDialog })),
        folder && (React.createElement(FolderUploadDialog, { parentFolder: folder, folderInputRef: folderInputRef, onCreateFolder: () => findFilesWithParams(), onDismiss: (progress, selectedFolder) => {
                handleProgressCount(progress, selectedFolder === folder);
            } })),
        isOpenCreateDialog && (React.createElement(CreateFolderDialog, { folderId: folderId, open: isOpenCreateDialog, onCancel: () => setIsOpenCreateDialog(false), onCreated: folder => {
                findFilesWithParams();
                setIsOpenCreateDialog(false);
                setSuccessMessage(`New Folder "${folder.name}" created.`);
            } })),
        React.createElement(PageTemplate, null,
            React.createElement(PageTemplateHeader, null,
                React.createElement(StyledTitle, null,
                    view !== ViewEnum.Board && folder && folder.id !== null && (React.createElement(DocumentBreadcrumbs, { fileId: folder.id, root: { label: 'Documents', path: basePath } })),
                    React.createElement("h2", null, view !== ViewEnum.Board && (folder === null || folder === void 0 ? void 0 : folder.name)
                        ? folder.name
                        : 'Documents')),
                React.createElement(Box, { ml: 2 },
                    React.createElement(ButtonGroup, { variant: 'contained', color: 'primary' },
                        React.createElement(Button, { size: 'small', onClick: () => setView(ViewEnum.List) },
                            React.createElement(ViewList, null)),
                        React.createElement(Button, { size: 'small', onClick: () => setView(ViewEnum.Board) },
                            React.createElement(ViewColumn, null)))),
                React.createElement(SearchBox, { value: search, onSearch: setSearch }, view === ViewEnum.Board && (React.createElement(DocumentFilter, { taskTypeId: taskTypeId, onFilter: options => {
                        const { assigneeIds, dueDateFrom, dueDateTo, taskTypeId } = options;
                        const params = Object.assign(Object.assign({}, apiParams), { dueDateFrom,
                            dueDateTo,
                            assigneeIds });
                        assigneeIds.length === 0 && delete params.assigneeIds;
                        !dueDateFrom && delete params.dueDateFrom;
                        !dueDateTo && delete params.dueDateTo;
                        setApiParams(params);
                        setTaskTypeId(taskTypeId);
                    } })))),
            React.createElement(PageTemplateAlert, null),
            view === ViewEnum.Board && (React.createElement(PageTemplateDashboard, null,
                React.createElement(TaskBoard, { apiParams: apiParams, taskTypeId: taskTypeId, onSelectTask: (task, manager) => __awaiter(void 0, void 0, void 0, function* () {
                        const file = yield manager.findAsync(task.id);
                        file && history.push(toFilePath(file.id));
                    }) }))),
            view !== ViewEnum.Board && (React.createElement(PageTemplateBodyCard, null,
                React.createElement(DocumentList, { files: files, isLoading: isLoading, selectedIds: selectedIds, order: order, orderBy: orderBy, setOrder: setOrder, setOrderBy: setOrderBy, onSelectionChange: setSelectedIds, onRowClick: file => {
                        const isDifferent = file.id !== (selected === null || selected === void 0 ? void 0 : selected.id);
                        setSelected(isDifferent ? file : undefined);
                        setIsOpenDrawer(isDifferent);
                    }, onRowDoubleClick: file => {
                        // clear search to show files within folder
                        // instead of searched files
                        file.isFolder && search && setSearch('');
                        const path = file.isFolder
                            ? `${basePath}/folder/${file.id}`
                            : toFilePath(file.id);
                        history.push(path);
                    } }),
                React.createElement(DocumentMoveDialog, { open: isOpenMoveDialog, fileIds: selectedIds, onCancel: () => setIsOpenMoveDialog(false), onMove: () => {
                        setIsOpenMoveDialog(false);
                        setSuccessMessage('Moved files successfully.');
                        refreshList();
                    } })))),
        selected && (React.createElement(DocumentInfoDrawer, { file: selected, open: isOpenDrawer, onClose: closeDrawer, onMove: () => {
                setIsOpenMoveDialog(true);
                setSelectedIds([selected.id]);
            }, onSuccess: message => {
                closeDrawer();
                findFilesWithParams();
                setSuccessMessage(message);
                // clear selected on rename or delete
                setSelectedIds([]);
            } })),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, error === null || error === void 0 ? void 0 : error.message),
        React.createElement(AppSnackbar, { severity: 'success', open: !!successMessage, onClose: () => setSuccessMessage('') }, successMessage)));
};
export default DocumentListPage;
//# sourceMappingURL=DocumentListPage.js.map